import React, { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { captureError } from '../services/error-tracking.service';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  retryCount: number;
}

declare global {
  interface Window {
    OpenReplay?: {
      handleError: (error: Error) => void;
    };
  }
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static getDerivedStateFromError(_error: Error): Partial<State> {
    return { hasError: true };
  }

  private isDomManipulationError(error: Error): boolean {
    const msg = error.message || '';
    return (
      msg.includes('insertBefore') ||
      msg.includes('removeChild') ||
      msg.includes('appendChild') ||
      msg.includes('is not a child of this node')
    );
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // DOM manipulation errors (caused by translators/extensions modifying the DOM)
    // Retry once silently, then force reload if it persists
    if (this.isDomManipulationError(error)) {
      if (this.state.retryCount === 0) {
        // First attempt: silent retry
        console.warn('DOM manipulation error detected, retrying silently...');
        captureError(error, 'system_error', 'medium', {
          component: 'ErrorBoundary',
          action: 'dom_error_auto_retry',
        });
        this.setState({ hasError: false, error: null, errorInfo: null, retryCount: 1 });
        return;
      }
      // Second attempt failed: force reload to clear corrupted DOM
      console.warn('DOM manipulation error persisted, reloading page...');
      captureError(error, 'system_error', 'high', {
        component: 'ErrorBoundary',
        action: 'dom_error_force_reload',
      });
      window.location.reload();
      return;
    }

    this.setState({
      error,
      errorInfo,
    });

    // Send to our /logs/errors API (captured by monitor)
    captureError(error, 'system_error', 'critical', {
      component: 'ErrorBoundary',
      action: 'react_crash',
      additionalInfo: {
        componentStack: errorInfo.componentStack || '',
      },
    });

    // Send to OpenReplay
    if (window.OpenReplay) {
      window.OpenReplay.handleError(error);
    }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>

            <h1 className="text-2xl font-bold text-gray-900 mb-2">Ops! Algo deu errado</h1>

            <p className="text-gray-600 mb-6">
              Encontramos um problema ao processar sua solicitação. Não se preocupe, seus dados estão seguros.
            </p>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="mb-6 p-4 bg-gray-100 rounded-lg text-left">
                <p className="text-xs font-mono text-red-600 mb-2">{this.state.error.toString()}</p>
                {this.state.errorInfo && (
                  <details className="text-xs font-mono text-gray-600">
                    <summary className="cursor-pointer">Stack Trace</summary>
                    <pre className="mt-2 whitespace-pre-wrap">{this.state.errorInfo.componentStack}</pre>
                  </details>
                )}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={this.handleRetry}
                className="flex-1 bg-pink-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-pink-700 transition-colors"
              >
                Tentar Novamente
              </button>
              <button
                onClick={this.handleGoHome}
                className="flex-1 bg-gray-200 text-gray-800 py-3 px-6 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
              >
                Voltar ao Início
              </button>
            </div>

            <p className="mt-6 text-sm text-gray-500">Se o problema persistir, entre em contato com nosso suporte.</p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Hook version for functional components
export function useErrorHandler() {
  const [error, setError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    if (error) {
      throw error;
    }
  }, [error]);

  return setError;
}
